import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { api, EVENT_TYPES, STATUS_LABELS } from "../lib/api";
import { ImageUpload } from "../components/ImageUpload";

const empty = { title: "", artist_name: "", photo_url: "", date: "", time: "20h00", description: "", event_type: "Live music", status: "brouillon", cancellation_message: "" };

const statusColors = {
  publie: "text-[#009246] border-[#009246]/40",
  brouillon: "text-yellow-400 border-yellow-400/30",
  annule: "text-[#ff6b81] border-[#C8102E]/40",
  complet: "text-[#D4AF37] border-[#D4AF37]/40",
};

export default function AdminPlanning() {
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState({});
  const [editing, setEditing] = useState(null);

  const load = () => {
    api.get("/admin/events").then((r) => setEvents(r.data)).catch(() => {});
    api.get("/admin/events/stats").then((r) => setStats(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...editing, photo_url: editing.photo_url || null, cancellation_message: editing.cancellation_message || null };
    try {
      if (editing.id) await api.put(`/admin/events/${editing.id}`, payload);
      else await api.post("/admin/events", payload);
      toast.success("Soirée enregistrée — le site est mis à jour automatiquement");
      setEditing(null);
      load();
    } catch { toast.error("Erreur lors de l'enregistrement"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette soirée ?")) return;
    try { await api.delete(`/admin/events/${id}`); toast.success("Supprimée"); load(); } catch { toast.error("Erreur"); }
  };

  const set = (k) => (e) => setEditing({ ...editing, [k]: e.target.value });

  return (
    <div data-testid="admin-planning">
      <div className="flex items-center justify-between mb-2">
        <h1 className="font-serif-elegant text-3xl font-bold">Planning &amp; Artistes</h1>
        <button data-testid="event-add-btn" onClick={() => setEditing({ ...empty })} className="btn-gold !py-2 text-sm"><Plus size={16} /> Nouvelle soirée</button>
      </div>
      <p className="text-sm text-gray-400 mb-8">Gérez le planning des soirées, les artistes et leurs photos. Le site et l'app se mettent à jour instantanément.</p>

      {editing && (
        <form onSubmit={save} className="ic-card !transform-none p-6 mb-8 space-y-4" data-testid="event-edit-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input data-testid="event-form-title" required className="ic-input" placeholder="Titre de la soirée *" value={editing.title} onChange={set("title")} />
            <input data-testid="event-form-artist" required className="ic-input" placeholder="Nom de l'artiste / groupe *" value={editing.artist_name} onChange={set("artist_name")} />
            <select data-testid="event-form-type" className="ic-input" value={editing.event_type} onChange={set("event_type")}>
              {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <input data-testid="event-form-date" required type="date" className="ic-input" value={editing.date} onChange={set("date")} />
            <input data-testid="event-form-time" required className="ic-input" placeholder="Heure (ex: 20h30) *" value={editing.time} onChange={set("time")} />
            <select data-testid="event-form-status" className="ic-input" value={editing.status} onChange={set("status")}>
              <option value="brouillon">Brouillon</option>
              <option value="publie">Publié</option>
              <option value="complet">Complet</option>
              <option value="annule">Annulé</option>
            </select>
          </div>
          <div>
            <label className="gold-label block mb-2">Photo de l'artiste / du groupe</label>
            <ImageUpload testId="event-form-photo" value={editing.photo_url} onChange={(u) => setEditing({ ...editing, photo_url: u })} />
          </div>
          <textarea data-testid="event-form-description" rows="2" className="ic-input" placeholder="Description de la soirée" value={editing.description} onChange={set("description")} />
          {editing.status === "annule" && (
            <input data-testid="event-form-cancellation" className="ic-input" placeholder="Message d'annulation affiché aux clients" value={editing.cancellation_message || ""} onChange={set("cancellation_message")} />
          )}
          <div className="flex gap-3">
            <button type="submit" data-testid="event-form-save" className="btn-gold !py-2 text-sm">Enregistrer</button>
            <button type="button" onClick={() => setEditing(null)} className="btn-outline-gold !py-2 text-sm">Annuler</button>
          </div>
        </form>
      )}

      <div className="space-y-3">
        {events.map((e) => (
          <div key={e.id} className="ic-card !transform-none p-4 flex items-center gap-4" data-testid={`admin-event-${e.id}`}>
            {e.photo_url ? (
              <img src={e.photo_url} alt={e.artist_name} className="w-16 h-16 rounded-lg object-cover border border-white/10" />
            ) : (
              <div className="w-16 h-16 rounded-lg bg-[#1a1a1a] border border-white/10" />
            )}
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{e.title}</p>
              <p className="text-sm text-gray-400">{e.artist_name} · {e.event_type}</p>
              <p className="text-xs text-gray-500">{e.date} à {e.time}</p>
              <p className="text-xs mt-1" data-testid={`event-stats-${e.id}`}>
                <span className="text-[#D4AF37]">{stats[e.id]?.reservations || 0} résa</span>
                <span className="text-gray-500"> · </span>
                <span className="text-[#009246]">{stats[e.id]?.couverts || 0} couverts</span>
              </p>
            </div>
            <span className={`text-xs border rounded-full px-3 py-1 whitespace-nowrap ${statusColors[e.status]}`}>{STATUS_LABELS[e.status]}</span>
            <div className="flex gap-1">
              <button data-testid={`event-edit-${e.id}`} onClick={() => setEditing({ ...e })} className="p-2 text-gray-400 hover:text-[#D4AF37]"><Pencil size={16} /></button>
              <button data-testid={`event-delete-${e.id}`} onClick={() => remove(e.id)} className="p-2 text-gray-400 hover:text-[#ff6b81]"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
